import type { MeResponse } from '@stalvi/contracts';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { signOut as fbSignOut, onAuthChanged } from '../lib/firebase';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'not_allowed' }
  | { status: 'ready'; me: MeResponse };

/**
 * Pure mapper: translates an error thrown by `api.me()` into the terminal
 * AuthState the hook should land on. 403 means the email is not on the static
 * allowlist; anything else is treated as "signed out" (network blip, expired
 * session, etc.) so the user can retry from the login screen.
 */
function mapMeErrorToState(err: unknown): AuthState {
  const status = (err as { status?: number }).status;
  if (status === 403) return { status: 'not_allowed' };
  return { status: 'signed_out' };
}

/**
 * Cancellation token shared between the effect and its async helpers. Mirrors
 * the inline `let cancelled = false` pattern previously used in the hook so
 * the cleanup (`unsub` + flip `cancelled` to true) keeps the same semantics.
 */
type CancelToken = { cancelled: boolean };

/**
 * Resolves the authenticated user. The new `/v1/me` returns `{ email }` only —
 * no role/claim-drift signal — so the id-token force-refresh dance is gone; the
 * only token refresh left is the api.ts 401 retry. On cancellation returns
 * `null`; the caller treats that as "do nothing".
 */
async function loadMe(token: CancelToken): Promise<MeResponse | null> {
  const me = await api.me();
  if (token.cancelled) return null;
  return me;
}

export function useAuth(): AuthState & {
  signOut: () => Promise<void>;
} {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let activeToken: CancelToken = { cancelled: false };
    const unsub = onAuthChanged(async (u) => {
      // Cancel any in-flight loadMeWithRefresh from a previous callback so its
      // resolution can't clobber state set by this newer callback.
      activeToken.cancelled = true;
      const token: CancelToken = { cancelled: false };
      activeToken = token;
      const safeSet = (next: AuthState) => {
        if (!token.cancelled) setState(next);
      };
      if (!u) {
        safeSet({ status: 'signed_out' });
        return;
      }
      // Transitioning from signed_out/ready → (re)checking whitelist.
      // Switch to 'loading' so <Shell> unmounts <Login> (clearing its local
      // "Conectando…" spinner) while /me is in flight. Without this the user
      // stays on the Login page with a stale spinner until /me resolves.
      safeSet({ status: 'loading' });
      try {
        const me = await loadMe(token);
        if (me === null) return;
        safeSet({ status: 'ready', me });
      } catch (e) {
        if (token.cancelled) return;
        safeSet(mapMeErrorToState(e));
      }
    });
    return () => {
      activeToken.cancelled = true;
      unsub();
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // best-effort
    }
    await fbSignOut();
    setState({ status: 'signed_out' });
  }, []);

  return { ...state, signOut };
}
